import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSnackBar } from '@angular/material/snack-bar'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { debounceTime, distinctUntilChanged } from 'rxjs'

import {
  DialogResult,
  MessageDialogService,
} from '../../../components/message-dialog/message-dialog.service'
import {
  KpiDashboardDefinition,
  KpiDashboardService,
  KpiKnowledgeView,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { captureException } from '../../../util/captureException'
import {
  buildKpiKnowledgeInput,
  filterKpiKnowledges,
  formatKpiKnowledgeCount,
} from './kpi-knowledge-settings.logic'

const KNOWLEDGE_NAME_MAX_LENGTH = 200
const KNOWLEDGE_DESCRIPTION_MAX_LENGTH = 2000

@UntilDestroy()
@Component({
  selector: 'app-kpi-knowledge-settings',
  standalone: true,
  imports: [MatIconModule, MatProgressSpinnerModule, ReactiveFormsModule],
  templateUrl: './kpi-knowledge-settings.component.html',
  styleUrl: './kpi-knowledge-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiKnowledgeSettingsComponent implements OnInit {
  @Input({ required: true }) dashboard!: KpiDashboardDefinition

  readonly nameMaxLength = KNOWLEDGE_NAME_MAX_LENGTH
  readonly descriptionMaxLength = KNOWLEDGE_DESCRIPTION_MAX_LENGTH
  readonly searchControl = new FormControl('', { nonNullable: true })
  readonly knowledgeForm = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [
        nonBlankValidator(),
        Validators.maxLength(KNOWLEDGE_NAME_MAX_LENGTH),
      ],
    }),
    situation: createDescriptionControl(),
    badResponse: createDescriptionControl(),
    goodResponse: createDescriptionControl(),
    reason: createDescriptionControl(),
  })

  knowledges: KpiKnowledgeView[] = []
  filteredKnowledges: KpiKnowledgeView[] = []
  appliedSearchQuery = ''
  isLoading = true
  isFormOpen = false
  isSaving = false
  deletingKnowledgeId: string | null = null
  editingKnowledgeId: string | null = null
  loadErrorMessage = ''
  formErrorMessage = ''
  deleteErrorMessage = ''

  constructor(
    private kpiDashboardService: KpiDashboardService,
    private messageDialogService: MessageDialogService,
    private snackBar: MatSnackBar,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), untilDestroyed(this))
      .subscribe((searchQuery) => {
        this.appliedSearchQuery = searchQuery
        this.recalculateFilteredKnowledges()
        this.changeDetectorRef.markForCheck()
      })
    this.loadKnowledges()
  }

  get countText(): string {
    return formatKpiKnowledgeCount(
      this.filteredKnowledges.length,
      this.knowledges.length,
      this.appliedSearchQuery,
    )
  }

  get isSearching(): boolean {
    return this.appliedSearchQuery.trim() !== ''
  }

  /** 読み込みエラー後にナレッジ一覧を再取得する。 */
  retry(): void {
    this.loadKnowledges()
  }

  /** 新規作成フォームの開閉状態を切り替える。 */
  toggleCreateForm(): void {
    if (this.isFormOpen) {
      this.closeForm()
      return
    }
    this.openCreateForm()
  }

  /** 入力を初期化して新規作成フォームを開く。 */
  openCreateForm(): void {
    this.editingKnowledgeId = null
    this.formErrorMessage = ''
    this.knowledgeForm.reset()
    this.isFormOpen = true
  }

  /** 指定ナレッジをフォームへ設定して編集を開始する。 */
  startEditing(knowledge: KpiKnowledgeView): void {
    if (this.isSaving || this.deletingKnowledgeId != null) {
      return
    }
    if (this.isFormOpen && this.knowledgeForm.dirty) {
      this.messageDialogService
        .showConfirm(
          '入力中の内容が失われます。このナレッジの編集に切り替えますか？',
          {
            title: '入力中の内容があります',
            primaryButtonText: '編集に切り替える',
            secondaryButtonText: '入力を続ける',
          },
        )
        .pipe(untilDestroyed(this))
        .subscribe((result) => {
          if (result === DialogResult.PrimaryButtonClicked) {
            this.openEditForm(knowledge)
          }
        })
      return
    }
    this.openEditForm(knowledge)
  }

  private openEditForm(knowledge: KpiKnowledgeView): void {
    this.editingKnowledgeId = knowledge.id
    this.formErrorMessage = ''
    this.knowledgeForm.setValue({
      name: knowledge.name,
      situation: knowledge.situation,
      badResponse: knowledge.badResponse,
      goodResponse: knowledge.goodResponse,
      reason: knowledge.reason,
    })
    this.knowledgeForm.markAsPristine()
    this.knowledgeForm.markAsUntouched()
    this.isFormOpen = true
    this.changeDetectorRef.markForCheck()
  }

  cancelEditing(): void {
    if (this.isSaving) {
      return
    }
    this.closeForm()
  }

  /** フォームを検証し、ナレッジを新規作成または更新する。 */
  save(): void {
    if (this.isSaving) {
      return
    }
    if (this.knowledgeForm.invalid) {
      this.knowledgeForm.markAllAsTouched()
      this.formErrorMessage = '入力内容を確認してください。'
      this.changeDetectorRef.markForCheck()
      return
    }

    const input = buildKpiKnowledgeInput(this.knowledgeForm.getRawValue())
    this.isSaving = true
    this.formErrorMessage = ''
    this.changeDetectorRef.markForCheck()
    const result$ =
      this.editingKnowledgeId == null
        ? this.kpiDashboardService.createKnowledge(this.dashboard.id, input)
        : this.kpiDashboardService.updateKnowledge(
            this.editingKnowledgeId,
            input,
          )

    result$.pipe(untilDestroyed(this)).subscribe({
      next: (savedKnowledge) => {
        const wasEditing = this.editingKnowledgeId != null
        this.knowledges = wasEditing
          ? this.knowledges.map((knowledge) =>
              knowledge.id === savedKnowledge.id ? savedKnowledge : knowledge,
            )
          : [savedKnowledge, ...this.knowledges]
        this.isSaving = false
        this.recalculateFilteredKnowledges()
        this.closeForm()
        this.snackBar.open(
          wasEditing ? 'ナレッジを更新しました。' : 'ナレッジを追加しました。',
          '閉じる',
          { duration: 3000 },
        )
        this.changeDetectorRef.markForCheck()
      },
      error: (error: unknown) => {
        captureException(error)
        this.isSaving = false
        this.formErrorMessage =
          'ナレッジを保存できませんでした。時間を置いて再度お試しください。'
        this.changeDetectorRef.markForCheck()
      },
    })
  }

  /** 確認ダイアログの承認後に指定ナレッジを削除する。 */
  requestDelete(knowledge: KpiKnowledgeView): void {
    if (this.deletingKnowledgeId != null || this.isSaving) {
      return
    }
    this.messageDialogService
      .showConfirm(
        `「${knowledge.name}」を削除します。削除したナレッジは元に戻せません。`,
        {
          title: 'ナレッジ削除の確認',
          primaryButtonText: '削除する',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result === DialogResult.PrimaryButtonClicked) {
          this.deleteKnowledge(knowledge)
        }
      })
  }

  /** ダッシュボードのナレッジ一覧を取得して検索結果を更新する。 */
  private loadKnowledges(): void {
    this.isLoading = true
    this.loadErrorMessage = ''
    this.kpiDashboardService
      .knowledges(this.dashboard.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (knowledges) => {
          this.knowledges = [...knowledges]
          this.isLoading = false
          this.recalculateFilteredKnowledges()
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => {
          captureException(error)
          this.isLoading = false
          this.loadErrorMessage = 'ナレッジを読み込めませんでした。'
          this.changeDetectorRef.markForCheck()
        },
      })
  }

  /** 指定ナレッジを論理削除し、ローカル一覧から取り除く。 */
  private deleteKnowledge(knowledge: KpiKnowledgeView): void {
    this.deletingKnowledgeId = knowledge.id
    this.deleteErrorMessage = ''
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .deleteKnowledge(knowledge.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (deleted) => {
          if (!deleted) {
            this.handleDeleteError(
              new Error('deleteKpiKnowledge returned false'),
            )
            return
          }
          this.knowledges = this.knowledges.filter(
            ({ id }) => id !== knowledge.id,
          )
          this.deletingKnowledgeId = null
          if (this.editingKnowledgeId === knowledge.id) {
            this.closeForm()
          }
          this.recalculateFilteredKnowledges()
          this.snackBar.open('ナレッジを削除しました。', '閉じる', {
            duration: 3000,
          })
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => this.handleDeleteError(error),
      })
  }

  private handleDeleteError(error: unknown): void {
    captureException(error)
    this.deletingKnowledgeId = null
    this.deleteErrorMessage =
      'ナレッジを削除できませんでした。時間を置いて再度お試しください。'
    this.changeDetectorRef.markForCheck()
  }

  private recalculateFilteredKnowledges(): void {
    this.filteredKnowledges = filterKpiKnowledges(
      this.knowledges,
      this.appliedSearchQuery,
    )
  }

  private closeForm(): void {
    this.isFormOpen = false
    this.editingKnowledgeId = null
    this.formErrorMessage = ''
    this.knowledgeForm.reset()
  }
}

/** 必須・最大文字数を設定した説明項目用 FormControl を生成する。 */
function createDescriptionControl(): FormControl<string> {
  return new FormControl('', {
    nonNullable: true,
    validators: [
      nonBlankValidator(),
      Validators.maxLength(KNOWLEDGE_DESCRIPTION_MAX_LENGTH),
    ],
  })
}

/** 空白だけの文字列を required エラーにする。 */
function nonBlankValidator(): ValidatorFn {
  return (control: AbstractControl<string>): ValidationErrors | null =>
    control.value.trim() === '' ? { required: true } : null
}
